import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { isVendedorScopedRole } from "@/types/auth";
import type { ConfirmacaoVendaStatus, SituacaoRenovacaoPedido, TipoNegocioPedido } from "@/lib/pedidosCiclo";
import { proximoNumeroCiclo } from "@/lib/pedidosCiclo";
import { buscarPedidoOrigemRenovacao } from "@/lib/resolverPedidoRenovacao";
import {
  calcularDiasAteVencimento,
  mensagemRenovacaoIndisponivel,
  podeIniciarRenovacao,
} from "@/lib/renovacaoPrazo";
import { planoIncluiLimpeza, type LimpezaStatus } from "@/lib/planoLimpeza";
import { ativarSeguroAposPrimeiraParcela, criarEventoRenovacaoAgenda } from "@/hooks/useParcelas";
import { applyEscopoVendedorPedidosQuery } from "@/lib/vendedorScope";

export interface Pedido {
  id: number;
  numero_pedido: string;
  orcamento_id?: number;
  projeto_id?: number;
  cliente_id: number;
  vendedor_id?: string | null;
  tipo_negocio?: TipoNegocioPedido;
  numero_ciclo?: number | null;
  pedido_anterior_id?: number | null;
  situacao_renovacao?: SituacaoRenovacaoPedido;
  data_nao_renovado?: string | null;
  motivo_nao_renovacao?: string | null;
  data_renovacao?: string | null;
  limpeza_status?: LimpezaStatus | null;
  limpeza_data_agendada?: string | null;
  limpeza_observacoes?: string | null;
  limpeza_fotos_antes?: string[] | null;
  limpeza_fotos_depois?: string[] | null;
  confirmacao_status?: ConfirmacaoVendaStatus | null;
  confirmado_por?: string | null;
  confirmado_em?: string | null;
  confirmacao_cancelado_por?: string | null;
  confirmacao_cancelado_em?: string | null;
  confirmacao_motivo_cancelamento?: string | null;
  pedido_anterior?: { id: number; numero_pedido?: string } | null;
  cliente?: {
    id: number;
    nome: string;
    email?: string;
    telefone?: string;
    cidade?: string;
    estado?: string;
  };
  orcamento?: {
    id: number;
    valor_total?: number;
    valor_seguro?: number;
    quantidade_placas?: number;
    vigencia_anos?: number;
    plano_id?: number | string;
    vendedor_id?: string | null;
    plano?: {
      id?: string;
      nome?: string;
      inclui_limpeza?: boolean;
    };
  };
  projeto?: {
    id: number;
    valor_projeto?: number;
    total_paineis?: number;
    observacoes?: string;
    status?: string;
    empresa_responsavel?: string | null;
    empresa_projeto_externo_id?: string | null;
    id_projeto_elevo?: string | null;
  };
  status: string;
  data_pedido: string;
  aprovado_por?: string;
  observacoes?: string;
  valor_total?: number;
  prazo_entrega?: number;
  created_at: string;
  updated_at: string;
}

/** Resolve se o orçamento/plano inclui limpeza (para gravar no pedido). */
export async function resolverLimpezaStatusParaOrcamento(
  orcamentoId: number
): Promise<LimpezaStatus | null> {
  const { data: orc } = await supabase
    .from("orcamentos")
    .select("plano_id")
    .eq("id", orcamentoId)
    .maybeSingle();

  if (!orc?.plano_id) return null;

  const { data: plano } = await supabase
    .from("planos_seguro")
    .select("id, nome, inclui_limpeza")
    .eq("id", orc.plano_id)
    .maybeSingle();

  return planoIncluiLimpeza(plano) ? "pendente" : null;
}

export type PedidosSort = {
  field:
    | "id"
    | "created_at"
    | "data_pedido"
    | "status"
    | "valor_total"
    | "paineis"
    | "cliente_nome"
    | "valor_seguro";
  direction: "asc" | "desc";
};

export type PedidosRecebimentoFiltro = "todos" | "recebido" | "nao_recebido";
export type PedidosTipoNegocioFiltro = "todos" | TipoNegocioPedido;
export type PedidosListagemModo = "ativos" | "nao_renovados";
export type PedidosConfirmacaoFiltro =
  | "todos"
  | "aguardando"
  | "confirmada"
  | "cancelada"
  | "sem_financeiro";

export type PedidosFiltroParams = {
  periodo?: { dataInicio?: string; dataFim?: string } | null; // pedidos.data_pedido (yyyy-mm-dd)
  status?: string | null;                                     // pedidos.status
  criadoPorId?: string | null;                                // pedidos.vendedor_id
  planoId?: string | null;                                    // orcamento.plano_id
  estado?: string | null;                                     // cliente.estado (UF sigla)
  cidade?: string | null;                                     // cliente.cidade
  paineis?: { min?: number; max?: number } | null;            // projeto.total_paineis
  valorProjeto?: { min?: number; max?: number } | null;       // projeto.valor_projeto
  valorSeguro?: { min?: number; max?: number } | null;        // orcamento.valor_seguro
  /** 1ª parcela / à vista recebida (seguro pode valer) */
  recebimento?: PedidosRecebimentoFiltro | null;
  /** Primeira venda ou renovação */
  tipoNegocio?: PedidosTipoNegocioFiltro | null;
  /** ativos = apólice vigente (em_vigencia/aguardando); nao_renovados = arquivo */
  listagem?: PedidosListagemModo | null;
  /** Confirmação de venda pela gerência */
  confirmacao?: PedidosConfirmacaoFiltro | null;
};

/** Pedidos com primeira entrada recebida (financeiro, parcelas ou contrato com seguro ativado). */
export async function fetchPedidoIdsComPrimeiroRecebimento(): Promise<number[]> {
  const ids = new Set<number>();

  const { data: transacoes, error: errTrans } = await supabase
    .from("transacoes_financeiras")
    .select("pedido_id, numero_parcela, total_parcelas")
    .eq("tipo", "entrada")
    .eq("status", "pago")
    .not("pedido_id", "is", null);

  if (errTrans) {
    console.error("Erro ao buscar transações para filtro recebimento:", errTrans);
    throw errTrans;
  }

  for (const t of transacoes || []) {
    const pedidoId = Number(t.pedido_id);
    if (!pedidoId) continue;
    const numParcela = t.numero_parcela ?? 1;
    const totalParcelas = t.total_parcelas ?? 1;
    if (numParcela === 1 || totalParcelas <= 1) {
      ids.add(pedidoId);
    }
  }

  const { data: parcelas, error: errParc } = await supabase
    .from("parcelas")
    .select("pedido_id")
    .eq("status", "pago")
    .eq("numero_parcela", 1)
    .not("pedido_id", "is", null);

  if (errParc) {
    console.error("Erro ao buscar parcelas para filtro recebimento:", errParc);
    throw errParc;
  }

  for (const p of parcelas || []) {
    const pedidoId = Number(p.pedido_id);
    if (pedidoId) ids.add(pedidoId);
  }

  const { data: contratos, error: errContr } = await supabase
    .from("contratos")
    .select("pedido_id")
    .not("pedido_id", "is", null)
    .or("seguro_ativado.eq.true,primeira_parcela_paga.eq.true");

  if (errContr) {
    console.error("Erro ao buscar contratos para filtro recebimento:", errContr);
    throw errContr;
  }

  for (const c of contratos || []) {
    const pedidoId = Number(c.pedido_id);
    if (pedidoId) ids.add(pedidoId);
  }

  return Array.from(ids);
}

/** Aplica filtro Recebido / Não recebido na query de pedidos. Retorna empty=true se não houver resultados. */
export async function aplicarFiltroRecebimentoPedidos(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  query: any,
  recebimento?: PedidosRecebimentoFiltro | null
): Promise<{ query: any; empty: boolean }> {
  if (!recebimento || recebimento === "todos") {
    return { query, empty: false };
  }

  const ids = await fetchPedidoIdsComPrimeiroRecebimento();

  if (recebimento === "recebido") {
    if (ids.length === 0) return { query, empty: true };
    return { query: query.in("id", ids), empty: false };
  }

  if (ids.length === 0) {
    return { query, empty: false };
  }

  return {
    query: query.not("id", "in", `(${ids.join(",")})`),
    empty: false,
  };
}

function registrarDataRecebimento(
  map: Record<number, string | null>,
  pedidoId: number,
  data?: string | null
) {
  if (!pedidoId || !data) return;
  const dia = data.split("T")[0];
  if (!map[pedidoId] || dia < map[pedidoId]!) {
    map[pedidoId] = dia;
  }
}

/** Data do primeiro recebimento (1ª parcela / à vista) por pedido. */
export async function fetchDatasPrimeiroRecebimentoPorPedidos(
  pedidoIds: number[]
): Promise<Record<number, string | null>> {
  const result: Record<number, string | null> = {};
  const uniqueIds = [...new Set(pedidoIds.filter((id) => id > 0))];
  if (uniqueIds.length === 0) return result;
  uniqueIds.forEach((id) => {
    result[id] = null;
  });

  const chunkSize = 200;
  for (let i = 0; i < uniqueIds.length; i += chunkSize) {
    const chunk = uniqueIds.slice(i, i + chunkSize);

    const { data: transacoes, error: errTrans } = await supabase
      .from("transacoes_financeiras")
      .select("pedido_id, data_pagamento, numero_parcela, total_parcelas")
      .eq("tipo", "entrada")
      .eq("status", "pago")
      .in("pedido_id", chunk);

    if (errTrans) throw errTrans;

    for (const t of transacoes || []) {
      const pedidoId = Number(t.pedido_id);
      const numParcela = t.numero_parcela ?? 1;
      const totalParcelas = t.total_parcelas ?? 1;
      if (numParcela === 1 || totalParcelas <= 1) {
        registrarDataRecebimento(result, pedidoId, t.data_pagamento);
      }
    }

    const { data: parcelas, error: errParc } = await supabase
      .from("parcelas")
      .select("pedido_id, data_pagamento")
      .eq("status", "pago")
      .eq("numero_parcela", 1)
      .in("pedido_id", chunk);

    if (errParc) throw errParc;

    for (const p of parcelas || []) {
      registrarDataRecebimento(result, Number(p.pedido_id), p.data_pagamento);
    }

    const { data: contratos, error: errContr } = await supabase
      .from("contratos")
      .select("pedido_id, data_primeira_parcela_paga, data_ativacao_seguro")
      .in("pedido_id", chunk);

    if (errContr) throw errContr;

    for (const c of contratos || []) {
      const pedidoId = Number(c.pedido_id);
      registrarDataRecebimento(result, pedidoId, c.data_primeira_parcela_paga);
      registrarDataRecebimento(result, pedidoId, c.data_ativacao_seguro);
    }
  }

  return result;
}

/** Datas de renovação por pedido (próprio pedido renovação ou origem já renovada). */
export async function fetchDatasRenovacaoPorPedidos(
  pedidoIds: number[]
): Promise<Record<number, string>> {
  const result: Record<number, string> = {};
  if (pedidoIds.length === 0) return result;

  const unique = [...new Set(pedidoIds)];
  const chunkSize = 80;

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);

    const { data: pedidos, error } = await supabase
      .from("pedidos")
      .select("id, data_renovacao, data_pedido, tipo_negocio")
      .in("id", chunk);

    if (error) throw error;

    for (const p of pedidos || []) {
      const id = Number(p.id);
      if (p.data_renovacao) {
        result[id] = p.data_renovacao;
      } else if (p.tipo_negocio === "renovacao" && p.data_pedido) {
        result[id] = p.data_pedido;
      }
    }

    const { data: filhos, error: errFilhos } = await supabase
      .from("pedidos")
      .select("pedido_anterior_id, data_pedido, data_renovacao")
      .in("pedido_anterior_id", chunk)
      .eq("tipo_negocio", "renovacao")
      .order("data_pedido", { ascending: false });

    if (errFilhos) throw errFilhos;

    for (const f of filhos || []) {
      const origemId = Number(f.pedido_anterior_id);
      if (!origemId || result[origemId]) continue;
      const data = f.data_renovacao || f.data_pedido;
      if (data) result[origemId] = data;
    }
  }

  return result;
}

/** Data fim do contrato (prazo de renovação do seguro) por pedido. */
export async function fetchDataFimRenovacaoPorPedidos(
  pedidoIds: number[]
): Promise<Record<number, string>> {
  const result: Record<number, string> = {};
  if (pedidoIds.length === 0) return result;

  const unique = [...new Set(pedidoIds)];
  const chunkSize = 80;

  for (let i = 0; i < unique.length; i += chunkSize) {
    const chunk = unique.slice(i, i + chunkSize);

    const { data: contratos, error } = await supabase
      .from("contratos")
      .select("pedido_id, data_fim")
      .in("pedido_id", chunk)
      .not("data_fim", "is", null)
      .order("data_fim", { ascending: false });

    if (error) throw error;

    for (const c of contratos || []) {
      const pid = Number(c.pedido_id);
      if (!pid || result[pid]) continue;
      if (c.data_fim) result[pid] = c.data_fim;
    }
  }

  const semContrato = unique.filter((id) => !result[id]);
  if (semContrato.length === 0) return result;

  const { data: contratosIds } = await supabase
    .from("contratos")
    .select("id, pedido_id")
    .in("pedido_id", semContrato);

  const contratoIds = (contratosIds || []).map((c) => c.id).filter(Boolean);
  if (contratoIds.length === 0) return result;

  const { data: eventos, error: errEv } = await supabase
    .from("agenda")
    .select("data_fim, contrato_id")
    .eq("tipo", "renovacao_seguro")
    .in("contrato_id", contratoIds)
    .not("data_fim", "is", null)
    .order("data_fim", { ascending: false });

  if (errEv) throw errEv;

  const pedidoPorContrato = new Map<number, number>();
  for (const c of contratosIds || []) {
    if (c.pedido_id) pedidoPorContrato.set(c.id, Number(c.pedido_id));
  }

  for (const ev of eventos || []) {
    const pid = ev.contrato_id ? pedidoPorContrato.get(ev.contrato_id) : undefined;
    if (!pid || result[pid] || !ev.data_fim) continue;
    result[pid] = ev.data_fim;
  }

  return result;
}

export async function assertPedidoNaJanelaRenovacao(pedidoId: number): Promise<void> {
  const map = await fetchDataFimRenovacaoPorPedidos([pedidoId]);
  const dataFim = map[pedidoId];
  if (!podeIniciarRenovacao(dataFim)) {
    const dias = calcularDiasAteVencimento(dataFim);
    throw new Error(mensagemRenovacaoIndisponivel(dias));
  }
}

export function usePedidosDataFimRenovacao(pedidoIds: number[]) {
  const sortedKey = [...new Set(pedidoIds)].sort((a, b) => a - b).join(",");

  return useQuery({
    queryKey: ["pedidos", "data-fim-renovacao", sortedKey],
    queryFn: () => fetchDataFimRenovacaoPorPedidos(pedidoIds),
    enabled: pedidoIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function usePedidosDatasRenovacao(pedidoIds: number[]) {
  const sortedKey = [...new Set(pedidoIds)].sort((a, b) => a - b).join(",");

  return useQuery({
    queryKey: ["pedidos", "data-renovacao", sortedKey],
    queryFn: () => fetchDatasRenovacaoPorPedidos(pedidoIds),
    enabled: pedidoIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function usePedidosPrimeiroRecebimento(pedidoIds: number[]) {
  const sortedKey = [...new Set(pedidoIds)].sort((a, b) => a - b).join(",");

  return useQuery({
    queryKey: ["pedidos", "primeiro-recebimento", sortedKey],
    queryFn: () => fetchDatasPrimeiroRecebimentoPorPedidos(pedidoIds),
    enabled: pedidoIds.length > 0,
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

// Hook para contar total de pedidos
export function usePedidosCount() {
  const { user, profile, loading } = useAuth();
  const isScoped = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!user?.id;

  return useQuery({
    queryKey: ['pedidos', 'count', isScoped ? user?.id : null],
    queryFn: async () => {
      let query = supabase
        .from('pedidos')
        .select('*', { count: 'exact', head: true });
      if (isScoped) {
        const scoped = await applyEscopoVendedorPedidosQuery(query, user!.id);
        query = scoped.query;
      }

      const { count, error } = await query;

      if (error) {
        console.error('❌ Erro ao contar pedidos:', error);
        throw error;
      }

      return count || 0;
    },
    enabled: !loading,
    staleTime: 60 * 1000, // 1 minuto - contagem muda menos frequentemente
    refetchOnWindowFocus: false,
  });
}

// Hook para buscar pedidos paginados
export function usePedidosPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string,
  sort?: PedidosSort | null,
  filtros?: PedidosFiltroParams | null
) {
  const { user, profile, loading } = useAuth();
  const isScoped = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!user?.id;

  return useQuery({
    queryKey: ['pedidos', 'paginated', page, limit, searchTerm, sort ?? null, filtros ?? null, isScoped ? user?.id : null],
    enabled: !loading,
    queryFn: async () => {
      console.log(`🔍 Buscando pedidos - Página ${page}, Limite ${limit}, Busca: ${searchTerm || 'nenhuma'}...`);
      
      try {
        const from = (page - 1) * limit;
        const to = from + limit - 1;

        // Detectar se há filtros que dependem de inner join nos embeds
        const projetoFilterPresent =
          (filtros?.paineis?.min ?? null) !== null ||
          (filtros?.paineis?.max ?? null) !== null ||
          (filtros?.valorProjeto?.min ?? null) !== null ||
          (filtros?.valorProjeto?.max ?? null) !== null;
        const orcamentoFilterPresent =
          (filtros?.valorSeguro?.min ?? null) !== null ||
          (filtros?.valorSeguro?.max ?? null) !== null ||
          !!filtros?.planoId;
        const clienteFilterPresent = !!(filtros?.estado || filtros?.cidade);

        const clienteEmbed = `cliente:clientes${clienteFilterPresent ? "!inner" : ""}(id, nome, email, telefone, cidade, estado)`;
        const projetoEmbed = `projeto:projetos${projetoFilterPresent ? "!inner" : ""}(id, valor_projeto, total_paineis, observacoes, status)`;
        // !orcamento_id evita ambiguidade com orcamentos.pedido_origem_id
        const orcamentoEmbed = `orcamento:orcamentos!orcamento_id${orcamentoFilterPresent ? "!inner" : ""}(id, valor_total, valor_seguro, quantidade_placas, vigencia_anos, plano_id, forma_pagamento, parcelas_cartao, numero_parcelas, vendedor_id)`;

        // Construir query base
        const listagem = filtros?.listagem ?? "ativos";

        let query = supabase
          .from('pedidos')
          .select(`
            *,
            ${clienteEmbed},
            ${orcamentoEmbed},
            ${projetoEmbed},
            pedido_anterior:pedidos!pedido_anterior_id(id, numero_pedido)
          `, { count: 'exact' });

        if (listagem === "nao_renovados") {
          query = query.eq("situacao_renovacao", "nao_renovado");
        } else {
          query = query.in("situacao_renovacao", ["em_vigencia", "aguardando_renovacao"]);
        }

        if (filtros?.tipoNegocio && filtros.tipoNegocio !== "todos") {
          query = query.eq("tipo_negocio", filtros.tipoNegocio);
        }

        if (filtros?.confirmacao && filtros.confirmacao !== "todos") {
          if (filtros.confirmacao === "sem_financeiro") {
            query = query.is("confirmacao_status", null);
          } else {
            query = query.eq("confirmacao_status", filtros.confirmacao);
          }
        }

        // Filtros de período (data_pedido)
        if (filtros?.periodo?.dataInicio) {
          query = query.gte('data_pedido', filtros.periodo.dataInicio);
        }
        if (filtros?.periodo?.dataFim) {
          query = query.lte('data_pedido', filtros.periodo.dataFim);
        }
        // Status
        if (filtros?.status && filtros.status !== 'todos') {
          query = query.eq('status', filtros.status);
        }
        // Criado por (pedidos.vendedor_id) — vendedor scoped: pedido OU orçamento OU projeto
        if (filtros?.criadoPorId) {
          query = query.eq('vendedor_id', filtros.criadoPorId);
        } else if (isScoped) {
          const scoped = await applyEscopoVendedorPedidosQuery(query, user!.id);
          query = scoped.query;
        }
        // Estado / Cidade (cliente.estado / cliente.cidade) — exige inner no cliente
        if (filtros?.estado) {
          query = query.eq('cliente.estado', filtros.estado.toUpperCase());
        }
        if (filtros?.cidade) {
          query = query.eq('cliente.cidade', filtros.cidade);
        }
        // Painéis (projeto.total_paineis) — exige inner join no projeto
        if (typeof filtros?.paineis?.min === 'number' && !Number.isNaN(filtros.paineis.min)) {
          query = query.gte('projeto.total_paineis', filtros.paineis.min);
        }
        if (typeof filtros?.paineis?.max === 'number' && !Number.isNaN(filtros.paineis.max)) {
          query = query.lte('projeto.total_paineis', filtros.paineis.max);
        }
        // Valor do Projeto (projeto.valor_projeto)
        if (typeof filtros?.valorProjeto?.min === 'number' && !Number.isNaN(filtros.valorProjeto.min)) {
          query = query.gte('projeto.valor_projeto', filtros.valorProjeto.min);
        }
        if (typeof filtros?.valorProjeto?.max === 'number' && !Number.isNaN(filtros.valorProjeto.max)) {
          query = query.lte('projeto.valor_projeto', filtros.valorProjeto.max);
        }
        // Tipo de plano (orcamento.plano_id) — exige inner join no orçamento
        if (filtros?.planoId) {
          query = query.eq('orcamento.plano_id', filtros.planoId);
        }
        // Valor do Seguro (orcamento.valor_seguro)
        if (typeof filtros?.valorSeguro?.min === 'number' && !Number.isNaN(filtros.valorSeguro.min)) {
          query = query.gte('orcamento.valor_seguro', filtros.valorSeguro.min);
        }
        if (typeof filtros?.valorSeguro?.max === 'number' && !Number.isNaN(filtros.valorSeguro.max)) {
          query = query.lte('orcamento.valor_seguro', filtros.valorSeguro.max);
        }

        const recvFilter = await aplicarFiltroRecebimentoPedidos(query, filtros?.recebimento ?? "todos");
        if (recvFilter.empty) {
          return { data: [], count: 0 };
        }
        query = recvFilter.query;

        // Aplicar busca se fornecido
        if (searchTerm && searchTerm.trim()) {
          const searchLower = searchTerm.trim();
          
          // Buscar em campos diretos primeiro
          if (!isNaN(Number(searchLower))) {
            // Se for número, buscar por ID
            query = query.eq('id', parseInt(searchLower));
          } else {
            // Se for texto, buscar em campos diretos e também em clientes relacionados
            const { data: clientesEncontrados } = await supabase
              .from('clientes')
              .select('id')
              .ilike('nome', `%${searchLower}%`);
            
            if (clientesEncontrados && clientesEncontrados.length > 0) {
              const clienteIds = clientesEncontrados.map(c => c.id);
              query = query.or(`numero_pedido.ilike.%${searchLower}%,status.ilike.%${searchLower}%,cliente_id.in.(${clienteIds.join(',')})`);
            } else {
              query = query.or(`numero_pedido.ilike.%${searchLower}%,status.ilike.%${searchLower}%`);
            }
          }
        }

        // Ordenar e paginar
        const sortField = sort?.field || "created_at";
        const sortAsc = (sort?.direction || "desc") === "asc";
        if (sortField === "cliente_nome") {
          query = query.order("nome", { ascending: sortAsc, foreignTable: "cliente" }).range(from, to);
        } else if (sortField === "valor_seguro") {
          query = query.order("valor_seguro", { ascending: sortAsc, foreignTable: "orcamento" }).range(from, to);
        } else if (sortField === "paineis") {
          query = query.order("total_paineis", { ascending: sortAsc, foreignTable: "projeto" }).range(from, to);
        } else {
          query = query.order(sortField, { ascending: sortAsc }).range(from, to);
        }

        const { data, error, count } = await query;

        if (error) {
          console.error('❌ Erro ao buscar pedidos:', error);
          throw error;
        }

        console.log(`✅ Pedidos encontrados na página ${page}:`, data?.length || 0, `Total: ${count || 0}`);
        return { data: ((data || []) as unknown) as Pedido[], count: count || 0 };
      } catch (error) {
        console.error('💥 Erro geral na busca de pedidos:', error);
        throw error;
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutos - reduz refetch excessivo
    refetchOnWindowFocus: false, // Desabilitado para reduzir egresso
    gcTime: 10 * 60 * 1000, // 10 minutos de cache
    retry: 2, // Reduzir tentativas
    retryDelay: 1000,
  });
}

// Hook legado - mantido para compatibilidade, mas agora usa paginação
export function usePedidos() {
  return usePedidosPaginated(1, 15);
}

export function usePedido(id: string) {
  return useQuery({
    queryKey: ['pedido', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('pedidos')
        .select(`
          *,
          cliente:clientes(*),
          orcamento:orcamentos!orcamento_id(*),
          projeto:projetos(*),
          aprovador:profiles(*)
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Erro ao buscar pedido:', error);
        throw error;
      }

      return data as Pedido;
    },
    enabled: !!id,
    staleTime: 5 * 60 * 1000, // 5 minutos
    gcTime: 10 * 60 * 1000, // 10 minutos de cache
  });
}

export type PedidoEquipamentoDetalhe = {
  id?: number;
  quantidade?: number;
  equipamento?: {
    id?: number;
    tipo?: string;
    marca?: string;
    modelo?: string;
    potencia?: string | null;
  } | null;
};

export type PedidoDetalhesCompleto = Pedido & {
  cliente?: Pedido["cliente"] & {
    cpf_cnpj?: string;
    endereco?: string;
    numero?: string;
    bairro?: string;
    cep?: string;
  };
  orcamento?: Pedido["orcamento"] & {
    forma_pagamento?: string | null;
    parcelas_cartao?: number | null;
    numero_parcelas?: number | null;
    plano?: {
      id?: string;
      nome?: string;
      inclui_limpeza?: boolean;
      vigencia_meses?: number;
    } | null;
  };
  vendedor?: { id: string; nome?: string | null; email?: string | null } | null;
  contratos?: Array<{
    id: number;
    numero_contrato: string;
    valor: number;
    status: string;
    data_inicio: string;
    data_fim?: string | null;
    arquivo_pdf_url?: string | null;
    arquivo_pdf_name?: string | null;
    seguro_ativado?: boolean | null;
    data_ativacao_seguro?: string | null;
  }>;
  equipamentos?: PedidoEquipamentoDetalhe[];
  transacoes?: Array<{
    id: number;
    tipo: string;
    valor: number;
    forma_pagamento?: string | null;
    data_vencimento?: string | null;
    data_pagamento?: string | null;
    status: string;
    numero_parcela?: number | null;
    total_parcelas?: number | null;
    observacoes?: string | null;
    nota_fiscal_url?: string | null;
    nota_fiscal_name?: string | null;
    notas_fiscais?: Array<{ url: string; nome: string }> | null;
  }>;
  parcelas?: Array<{
    id: number;
    numero_parcela: number;
    total_parcelas: number;
    valor: number;
    data_vencimento: string;
    data_pagamento?: string | null;
    status: string;
    forma_pagamento?: string | null;
    comprovante_pagamento_url?: string | null;
    comprovante_pagamento_name?: string | null;
  }>;
  forma_pagamento_nome?: string | null;
};

/** Detalhe completo do pedido para o dialog do olho (cliente, plano, equipamentos, contrato, financeiro). */
export function usePedidoDetalhes(pedidoId: number | null | undefined, enabled = true) {
  return useQuery({
    queryKey: ["pedido", "detalhes", pedidoId],
    enabled: enabled && !!pedidoId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<PedidoDetalhesCompleto> => {
      const id = Number(pedidoId);
      const { data: pedido, error } = await supabase
        .from("pedidos")
        .select(
          `
          *,
          cliente:clientes(*),
          orcamento:orcamentos!orcamento_id(
            *,
            plano:planos_seguro(id, nome, inclui_limpeza, vigencia_meses)
          ),
          projeto:projetos(*),
          pedido_anterior:pedidos!pedido_anterior_id(id, numero_pedido)
        `
        )
        .eq("id", id)
        .single();

      if (error) {
        console.error("Erro ao buscar detalhes do pedido:", error);
        throw error;
      }

      const orcamentoId = (pedido as any)?.orcamento_id ?? (pedido as any)?.orcamento?.id;
      const projetoId = (pedido as any)?.projeto_id ?? (pedido as any)?.projeto?.id;
      const vendedorId = (pedido as any)?.vendedor_id ?? (pedido as any)?.orcamento?.vendedor_id;
      const formaPagamentoId = (pedido as any)?.orcamento?.forma_pagamento as string | null | undefined;

      let equipamentos: PedidoEquipamentoDetalhe[] = [];
      if (orcamentoId) {
        const { data: eqsOrc } = await supabase
          .from("orcamento_equipamentos")
          .select("*, equipamento:equipamentos(*)")
          .eq("orcamento_id", orcamentoId);
        if (eqsOrc && eqsOrc.length > 0) {
          equipamentos = eqsOrc as PedidoEquipamentoDetalhe[];
        }
      }
      if (equipamentos.length === 0 && projetoId) {
        const { data: eqsProj } = await supabase
          .from("projeto_equipamentos")
          .select("*, equipamento:equipamentos(*)")
          .eq("projeto_id", projetoId);
        if (eqsProj) {
          equipamentos = eqsProj as PedidoEquipamentoDetalhe[];
        }
      }

      const [{ data: contratos }, { data: transacoes }, { data: parcelas }, vendedorRes, formaRes] =
        await Promise.all([
          supabase
            .from("contratos")
            .select(
              "id, numero_contrato, valor, status, data_inicio, data_fim, arquivo_pdf_url, arquivo_pdf_name, seguro_ativado, data_ativacao_seguro"
            )
            .eq("pedido_id", id)
            .order("created_at", { ascending: false }),
          supabase
            .from("transacoes_financeiras")
            .select(
              "id, tipo, valor, forma_pagamento, data_vencimento, data_pagamento, status, numero_parcela, total_parcelas, observacoes, nota_fiscal_url, nota_fiscal_name, notas_fiscais"
            )
            .eq("pedido_id", id)
            .order("numero_parcela", { ascending: true }),
          supabase
            .from("parcelas")
            .select(
              "id, numero_parcela, total_parcelas, valor, data_vencimento, data_pagamento, status, forma_pagamento, comprovante_pagamento_url, comprovante_pagamento_name"
            )
            .eq("pedido_id", id)
            .order("numero_parcela", { ascending: true }),
          vendedorId
            ? supabase.from("profiles").select("id, nome, email").eq("id", vendedorId).maybeSingle()
            : Promise.resolve({ data: null }),
          formaPagamentoId
            ? supabase
                .from("formas_pagamento")
                .select("id, nome")
                .eq("id", formaPagamentoId)
                .maybeSingle()
            : Promise.resolve({ data: null }),
        ]);

      // Se forma_pagamento não for UUID/id de formas_pagamento, usar o valor cru
      let formaNome = (formaRes as { data?: { nome?: string } | null })?.data?.nome ?? null;
      if (!formaNome && formaPagamentoId) {
        formaNome = formaPagamentoId;
      }

      return {
        ...(pedido as Pedido),
        vendedor: (vendedorRes as { data?: PedidoDetalhesCompleto["vendedor"] })?.data ?? null,
        contratos: (contratos as PedidoDetalhesCompleto["contratos"]) ?? [],
        equipamentos,
        transacoes: (transacoes as PedidoDetalhesCompleto["transacoes"]) ?? [],
        parcelas: (parcelas as PedidoDetalhesCompleto["parcelas"]) ?? [],
        forma_pagamento_nome: formaNome,
      };
    },
  });
}

export function useCriarPedido() {
  const queryClient = useQueryClient();
  const { user, profile } = useAuth();
  const isScoped = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!user?.id;

  return useMutation({
    mutationFn: async (pedidoData: {
      orcamento_id: number;
      projeto_id: number;
      cliente_id: number;
      observacoes?: string;
      valor_total?: number;
      prazo_entrega?: number;
    }) => {
      // Gerar número do pedido
      const dataAtual = new Date();
      const numeroPedido = `PED${dataAtual.getFullYear()}${String(dataAtual.getMonth() + 1).padStart(2, '0')}${String(dataAtual.getDate()).padStart(2, '0')}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;

      // Vendedor logado: sempre grava o próprio id (evita herdar orçamento de outro)
      let vendedorId: string | null = null;
      if (isScoped && user?.id) {
        vendedorId = user.id;
      } else {
        try {
          const { data: orc, error: orcErr } = await supabase
            .from("orcamentos")
            .select("vendedor_id")
            .eq("id", pedidoData.orcamento_id)
            .single();
          if (!orcErr) vendedorId = (orc as any)?.vendedor_id ?? null;
        } catch {
          vendedorId = null;
        }
        if (!vendedorId) {
          const { data: { user: authUser } } = await supabase.auth.getUser();
          if (authUser?.id) vendedorId = authUser.id;
        }
      }

      const limpezaStatus = await resolverLimpezaStatusParaOrcamento(pedidoData.orcamento_id);

      const dadosParaInserir = {
        ...pedidoData,
        vendedor_id: vendedorId,
        numero_pedido: numeroPedido,
        status: 'aprovado', // Mudar para 'aprovado' para aparecer na listagem
        data_pedido: dataAtual.toISOString().split('T')[0], // Formato YYYY-MM-DD
        tipo_negocio: 'primeira_venda' as const,
        situacao_renovacao: 'em_vigencia' as const,
        numero_ciclo: 1,
        ...(limpezaStatus ? { limpeza_status: limpezaStatus } : {}),
      };

      console.log('Criando pedido:', dadosParaInserir);

      const { data, error } = await supabase
        .from('pedidos')
        .insert(dadosParaInserir)
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar pedido:', error);
        throw error;
      }

      console.log('Pedido criado com sucesso:', data);
      // Agenda grava somente renovações; acompanhamento de pedido não é mais inserido
      return data;
    },
    onSuccess: (data) => {
      console.log('Pedido criado, invalidando queries...');
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
      queryClient.invalidateQueries({ queryKey: ['pedidos-limpeza'] });
      queryClient.invalidateQueries({ queryKey: ['orcamentos'] });
      queryClient.invalidateQueries({ queryKey: ['agenda'] });
      console.log('Queries invalidadas');
      
      // Forçar refetch imediato
      setTimeout(() => {
        console.log('Forçando refetch dos pedidos...');
        queryClient.refetchQueries({ queryKey: ['pedidos'] });
      }, 100);
    }
  });
}

export function useAtualizarPedido() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updateData }: { 
      id: number; 
      status?: string;
      observacoes?: string;
      prazo_entrega?: number;
      situacao_renovacao?: SituacaoRenovacaoPedido;
    }) => {
      const { data, error } = await supabase
        .from('pedidos')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedidos'] });
    }
  });
}

/** Marca o pedido (ciclo encerrado) como não renovado — vai para a tela Não renovados. */
export function useMarcarPedidoNaoRenovado() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      motivo,
    }: {
      pedidoId: number;
      motivo?: string;
    }) => {
      const hoje = new Date().toISOString().split("T")[0];
      const { data, error } = await supabase
        .from("pedidos")
        .update({
          situacao_renovacao: "nao_renovado",
          data_nao_renovado: hoje,
          motivo_nao_renovacao: motivo?.trim() || null,
        })
        .eq("id", pedidoId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

/** Vincula um pedido novo como renovação e marca o pedido anterior como renovado. */
export function useVincularPedidoRenovacao() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoNovoId,
      pedidoAnteriorId,
    }: {
      pedidoNovoId: number;
      pedidoAnteriorId: number;
    }) => {
      const { data: anterior, error: errAnt } = await supabase
        .from("pedidos")
        .select("id, situacao_renovacao")
        .eq("id", pedidoAnteriorId)
        .single();

      if (errAnt) throw errAnt;
      if (anterior?.situacao_renovacao === "nao_renovado") {
        throw new Error("O pedido de origem já está marcado como não renovado.");
      }

      const { data: novo, error: errNovo } = await supabase
        .from("pedidos")
        .update({
          tipo_negocio: "renovacao",
          pedido_anterior_id: pedidoAnteriorId,
          situacao_renovacao: "em_vigencia",
        })
        .eq("id", pedidoNovoId)
        .select()
        .single();

      if (errNovo) throw errNovo;

      const { error: errOrigem } = await supabase
        .from("pedidos")
        .update({ situacao_renovacao: "renovado" })
        .eq("id", pedidoAnteriorId);

      if (errOrigem) throw errOrigem;

      return novo;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

export type EquipamentoRenovacaoInput = {
  equipamento_id: number;
  quantidade: number;
  preco_unitario?: number | null;
};

export type CriarPedidoRenovacaoResult = {
  orcamentoNovo: { id: number };
  pedidoAnteriorId: number;
  numeroCiclo: number;
};

export type CriarPropostaRenovacaoInput = {
  pedidoAnteriorId: number;
  planoId?: string | null;
  quantidadePlacas?: number | null;
  vigenciaAnos?: number | null;
  valorTabela: number;
  valorFinal: number;
  observacoesExtras?: string;
  equipamentos?: EquipamentoRenovacaoInput[];
  formaPagamento?: string | null;
  parcelasCartao?: number | null;
  numeroParcelas?: number | null;
  /** Reabertura a partir de Não renovados: continua o ciclo, ignora janela D-30 */
  permitirArquivoNaoRenovado?: boolean;
};

/**
 * Cria proposta de renovação: orçamento pendente (não auto-aprovado).
 * O pedido Nª venda só nasce na aprovação do orçamento.
 */
export function useCriarPedidoRenovacao() {
  const queryClient = useQueryClient();
  const { user: authUser, profile } = useAuth();
  const isScoped = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!authUser?.id;

  return useMutation({
    mutationFn: async ({
      pedidoAnteriorId,
      planoId,
      quantidadePlacas,
      vigenciaAnos,
      valorTabela,
      valorFinal,
      observacoesExtras,
      equipamentos,
      formaPagamento,
      parcelasCartao,
      numeroParcelas,
      permitirArquivoNaoRenovado = false,
    }: CriarPropostaRenovacaoInput): Promise<CriarPedidoRenovacaoResult> => {
      const origem = await buscarPedidoOrigemRenovacao(pedidoAnteriorId);
      if (!origem) throw new Error("Pedido de origem não encontrado.");

      const origemNaoRenovada = origem.situacao_renovacao === "nao_renovado";
      if (origemNaoRenovada && !permitirArquivoNaoRenovado) {
        throw new Error("Este pedido está em Não renovados. Use Vender novamente nessa tela.");
      }
      if (!origemNaoRenovada) {
        await assertPedidoNaJanelaRenovacao(pedidoAnteriorId);
      }
      if (origem.situacao_renovacao === "renovado") {
        throw new Error("Este pedido já foi renovado (ciclo encerrado).");
      }

      const { data: renovacaoExistente } = await supabase
        .from("pedidos")
        .select("id")
        .eq("pedido_anterior_id", pedidoAnteriorId)
        .in("situacao_renovacao", ["em_vigencia", "aguardando_renovacao"])
        .maybeSingle();

      if (renovacaoExistente?.id) {
        throw new Error(
          `Já existe pedido de renovação (#${String(renovacaoExistente.id).padStart(6, "0")}) para este ciclo.`
        );
      }

      const { data: orcPendente } = await supabase
        .from("orcamentos")
        .select("id")
        .eq("pedido_origem_id", pedidoAnteriorId)
        .eq("status", "pendente")
        .eq("ativo", true)
        .maybeSingle();

      if (orcPendente?.id) {
        throw new Error(
          `Já existe orçamento pendente de renovação (#${orcPendente.id}). Aprove ou recuse-o antes de criar outro.`
        );
      }

      if (!origem.cliente_id) {
        throw new Error("Pedido de origem sem cliente vinculado.");
      }

      const orc = origem.orcamento;
      const valorTabelaNum = Number(valorTabela);
      const valorFinalNum = Number(valorFinal);
      if (!valorFinalNum || valorFinalNum <= 0) {
        throw new Error("Informe um valor final maior que zero.");
      }
      if (!valorTabelaNum || valorTabelaNum <= 0) {
        throw new Error("Não foi possível determinar o valor de tabela.");
      }

      const descontoValor = Math.max(0, Math.round((valorTabelaNum - valorFinalNum) * 100) / 100);
      const numeroCiclo = proximoNumeroCiclo(origem.numero_ciclo, origem.tipo_negocio);

      const { data: { user } } = await supabase.auth.getUser();
      const vendedorId = origem.vendedor_id ?? orc?.vendedor_id ?? (isScoped ? authUser?.id : null) ?? user?.id ?? null;
      const descontoConcedidoPor = descontoValor > 0 ? (user?.id ?? authUser?.id ?? null) : null;

      const obsRenovacao = [
        `Proposta de renovação — ${numeroCiclo}ª venda (origem #${String(pedidoAnteriorId).padStart(6, "0")})`,
        observacoesExtras?.trim(),
      ]
        .filter(Boolean)
        .join(" — ");

      const { data: orcamentoNovo, error: errOrc } = await supabase
        .from("orcamentos")
        .insert({
          cliente_id: origem.cliente_id,
          projeto_id: origem.projeto_id ?? null,
          valor_total: valorFinalNum,
          valor_seguro: valorFinalNum,
          valor_tabela: valorTabelaNum,
          desconto_valor: descontoValor,
          desconto_concedido_por: descontoConcedidoPor,
          desconto_aplicado_em: descontoConcedidoPor ? new Date().toISOString() : null,
          desconto_motivo: descontoValor > 0 ? observacoesExtras?.trim() || null : null,
          quantidade_placas: quantidadePlacas ?? orc?.quantidade_placas ?? null,
          plano_id: planoId ?? orc?.plano_id ?? null,
          vigencia_anos: vigenciaAnos ?? orc?.vigencia_anos ?? 1,
          forma_pagamento: formaPagamento ?? orc?.forma_pagamento ?? null,
          parcelas_cartao: parcelasCartao ?? orc?.parcelas_cartao ?? null,
          numero_parcelas: numeroParcelas ?? orc?.numero_parcelas ?? null,
          observacoes: obsRenovacao,
          status: "pendente",
          ativo: true,
          vendedor_id: vendedorId,
          pedido_origem_id: pedidoAnteriorId,
          numero_ciclo: numeroCiclo,
          prazo_aprovacao: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        })
        .select("id")
        .single();

      if (errOrc) throw errOrc;

      let eqs = equipamentos ?? [];
      if (eqs.length === 0) {
        const orcOrigemId = orc?.id ?? origem.orcamento_id;
        if (orcOrigemId) {
          const { data: eqsOrc } = await supabase
            .from("orcamento_equipamentos")
            .select("equipamento_id, quantidade, preco_unitario")
            .eq("orcamento_id", orcOrigemId);
          eqs = (eqsOrc || []) as EquipamentoRenovacaoInput[];
        }
        if (eqs.length === 0 && origem.projeto_id) {
          const { data: eqsProj } = await supabase
            .from("projeto_equipamentos")
            .select("equipamento_id, quantidade, preco_unitario")
            .eq("projeto_id", origem.projeto_id);
          eqs = (eqsProj || []) as EquipamentoRenovacaoInput[];
        }
      }

      if (eqs.length > 0) {
        const rows = eqs
          .filter((e) => e.equipamento_id && e.quantidade > 0)
          .map((e) => ({
            orcamento_id: orcamentoNovo.id,
            equipamento_id: e.equipamento_id,
            quantidade: e.quantidade,
            preco_unitario: e.preco_unitario ?? 0,
          }));
        if (rows.length > 0) {
          const { error: errEq } = await supabase.from("orcamento_equipamentos").insert(rows);
          if (errEq) throw errEq;
        }
      }

      if (!origemNaoRenovada && origem.situacao_renovacao !== "aguardando_renovacao") {
        await supabase
          .from("pedidos")
          .update({ situacao_renovacao: "aguardando_renovacao" })
          .eq("id", pedidoAnteriorId)
          .neq("situacao_renovacao", "nao_renovado")
          .neq("situacao_renovacao", "renovado");
      }

      return {
        orcamentoNovo: orcamentoNovo as { id: number },
        pedidoAnteriorId,
        numeroCiclo,
      };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
      queryClient.invalidateQueries({ queryKey: ["orcamentos"] });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

/** Coloca pedido em aguardando renovação (ex.: perto do vencimento do seguro). */
export function useMarcarPedidoAguardandoRenovacao() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pedidoId: number) => {
      const { data, error } = await supabase
        .from("pedidos")
        .update({ situacao_renovacao: "aguardando_renovacao" })
        .eq("id", pedidoId)
        .neq("situacao_renovacao", "nao_renovado")
        .neq("situacao_renovacao", "renovado")
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
    },
  });
}

/** Pedidos elegíveis à aba Limpeza (plano PROTEÇÃO + LIMPEZA). */
export function usePedidosLimpezaPaginated(
  page: number = 1,
  limit: number = 15,
  searchTerm?: string
) {
  const { user, profile, loading } = useAuth();
  const isScoped = isVendedorScopedRole((profile?.user_role || profile?.role) as any) && !!user?.id;

  return useQuery({
    queryKey: ["pedidos-limpeza", "paginated", page, limit, searchTerm ?? "", isScoped ? user?.id : null],
    enabled: !loading,
    queryFn: async () => {
      const from = (page - 1) * limit;
      const to = from + limit - 1;

      let query = supabase
        .from("pedidos")
        .select(
          `
          *,
          cliente:clientes(id, nome, email, telefone, cidade, estado),
          orcamento:orcamentos!orcamento_id(
            id, valor_total, valor_seguro, quantidade_placas, vigencia_anos, plano_id, vendedor_id,
            plano:planos_seguro(id, nome, inclui_limpeza)
          ),
          projeto:projetos(id, valor_projeto, total_paineis, observacoes, status)
        `,
          { count: "exact" }
        )
        .not("limpeza_status", "is", null)
        .order("created_at", { ascending: false })
        .range(from, to);

      if (isScoped) {
        const scoped = await applyEscopoVendedorPedidosQuery(query, user!.id);
        query = scoped.query;
      }

      const term = searchTerm?.trim();
      if (term) {
        if (!isNaN(Number(term))) {
          const idSearch = parseInt(term, 10);
          query = query.or(`id.eq.${idSearch},numero_pedido.ilike.%${term}%`);
        } else {
          const { data: clientes } = await supabase
            .from("clientes")
            .select("id")
            .ilike("nome", `%${term}%`);
          const clienteIds = (clientes || []).map((c) => c.id);
          if (clienteIds.length > 0) {
            query = query.or(
              `numero_pedido.ilike.%${term}%,cliente_id.in.(${clienteIds.join(",")})`
            );
          } else {
            query = query.ilike("numero_pedido", `%${term}%`);
          }
        }
      }

      const { data, error, count } = await query;
      if (error) throw error;
      return { data: (data || []) as Pedido[], count: count || 0 };
    },
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useAgendarLimpeza() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      dataAgendada,
      observacoes,
    }: {
      pedidoId: number;
      dataAgendada: string;
      observacoes?: string | null;
    }) => {
      const payload: Record<string, unknown> = {
        limpeza_status: "agendada" as LimpezaStatus,
        limpeza_data_agendada: dataAgendada,
      };
      if (observacoes !== undefined) {
        payload.limpeza_observacoes = observacoes;
      }

      const { data, error } = await supabase
        .from("pedidos")
        .update(payload)
        .eq("id", pedidoId)
        .not("limpeza_status", "is", null)
        .select()
        .single();

      if (error) throw error;
      return data as Pedido;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
    },
  });
}

export function useConcluirLimpeza() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      fotosAntes,
      fotosDepois,
    }: {
      pedidoId: number;
      fotosAntes?: string[];
      fotosDepois?: string[];
    }) => {
      const payload: Record<string, unknown> = {
        limpeza_status: "concluida" as LimpezaStatus,
      };
      if (fotosAntes !== undefined) {
        payload.limpeza_fotos_antes = fotosAntes;
      }
      if (fotosDepois !== undefined) {
        payload.limpeza_fotos_depois = fotosDepois;
      }

      const { data, error } = await supabase
        .from("pedidos")
        .update(payload)
        .eq("id", pedidoId)
        .not("limpeza_status", "is", null)
        .select()
        .single();

      if (error) throw error;
      return data as Pedido;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
    },
  });
}

export function useSalvarFotosLimpeza() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      fotosAntes,
      fotosDepois,
    }: {
      pedidoId: number;
      fotosAntes: string[];
      fotosDepois: string[];
    }) => {
      const { data, error } = await supabase
        .from("pedidos")
        .update({
          limpeza_fotos_antes: fotosAntes,
          limpeza_fotos_depois: fotosDepois,
        })
        .eq("id", pedidoId)
        .not("limpeza_status", "is", null)
        .select()
        .single();

      if (error) throw error;
      return data as Pedido;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
    },
  });
}

export function useReabrirLimpeza() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (pedidoId: number) => {
      const { data, error } = await supabase
        .from("pedidos")
        .update({
          limpeza_status: "pendente" as LimpezaStatus,
          limpeza_data_agendada: null,
        })
        .eq("id", pedidoId)
        .not("limpeza_status", "is", null)
        .select()
        .single();

      if (error) throw error;
      return data as Pedido;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["pedidos-limpeza"] });
    },
  });
}

/** Marca pedido como aguardando confirmação (após gerar financeiro). */
export async function marcarPedidoAguardandoConfirmacao(pedidoId: number): Promise<void> {
  const { data: atual } = await supabase
    .from("pedidos")
    .select("confirmacao_status")
    .eq("id", pedidoId)
    .maybeSingle();

  if (atual?.confirmacao_status === "confirmada") return;

  const { error } = await supabase
    .from("pedidos")
    .update({
      confirmacao_status: "aguardando",
      confirmado_por: null,
      confirmado_em: null,
      confirmacao_cancelado_por: null,
      confirmacao_cancelado_em: null,
      confirmacao_motivo_cancelamento: null,
    })
    .eq("id", pedidoId);

  if (error) throw error;
}

export async function pedidoEstaAguardandoConfirmacao(
  pedidoId: number | null | undefined
): Promise<boolean> {
  if (!pedidoId) return false;
  const { data, error } = await supabase
    .from("pedidos")
    .select("confirmacao_status")
    .eq("id", pedidoId)
    .maybeSingle();
  if (error) throw error;
  return data?.confirmacao_status === "aguardando";
}

export function useConfirmarVendaPedido() {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();

  return useMutation({
    mutationFn: async (pedidoId: number) => {
      if (!isAdmin()) throw new Error("Apenas administradores podem confirmar a venda.");
      if (!user?.id) throw new Error("Usuário não autenticado.");

      const { data: pedido, error: errPed } = await supabase
        .from("pedidos")
        .select("id, confirmacao_status, cliente_id")
        .eq("id", pedidoId)
        .single();
      if (errPed) throw errPed;
      if (pedido.confirmacao_status !== "aguardando") {
        throw new Error("Pedido não está aguardando confirmação.");
      }

      const agora = new Date().toISOString();
      const dataPag = agora.split("T")[0];

      const { error: errUpd } = await supabase
        .from("pedidos")
        .update({
          confirmacao_status: "confirmada",
          confirmado_por: user.id,
          confirmado_em: agora,
        })
        .eq("id", pedidoId)
        .eq("confirmacao_status", "aguardando");
      if (errUpd) throw errUpd;

      // 1ª transação de entrada pendente → pago (se houver comprovante ou não)
      const { data: txs } = await supabase
        .from("transacoes_financeiras")
        .select("id, numero_parcela, total_parcelas, status, nota_fiscal_url, cliente_id")
        .eq("pedido_id", pedidoId)
        .eq("tipo", "entrada")
        .eq("status", "pendente")
        .order("numero_parcela", { ascending: true });

      const primeiraTx = (txs || []).find(
        (t) => (t.numero_parcela ?? 1) === 1 || (t.total_parcelas ?? 1) <= 1
      ) || (txs || [])[0];

      if (primeiraTx) {
        const { error: errTx } = await supabase
          .from("transacoes_financeiras")
          .update({ status: "pago", data_pagamento: dataPag })
          .eq("id", primeiraTx.id);
        if (errTx) throw errTx;

        const clienteId = Number(primeiraTx.cliente_id || pedido.cliente_id);
        if (clienteId) {
          try {
            await criarEventoRenovacaoAgenda(clienteId, dataPag, { pedidoId });
          } catch (e) {
            console.error("Erro ao criar evento de renovação:", e);
          }
        }
      }

      // 1ª parcela pendente do pedido → pago
      const { data: parcelas } = await supabase
        .from("parcelas")
        .select("id, numero_parcela, contrato_id, status")
        .eq("pedido_id", pedidoId)
        .eq("status", "pendente")
        .order("numero_parcela", { ascending: true });

      const primeiraParcela = (parcelas || []).find((p) => p.numero_parcela === 1) || (parcelas || [])[0];
      if (primeiraParcela) {
        const { error: errParc } = await supabase
          .from("parcelas")
          .update({ status: "pago", data_pagamento: dataPag })
          .eq("id", primeiraParcela.id);
        if (errParc) throw errParc;
      }

      // Ativar contrato (status → ativo + seguro) quando houver 1º recebimento confirmado
      let contratoId =
        primeiraParcela?.numero_parcela === 1 ? primeiraParcela.contrato_id : null;
      if (!contratoId) {
        const { data: contratoDoPedido } = await supabase
          .from("contratos")
          .select("id, seguro_ativado")
          .eq("pedido_id", pedidoId)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (contratoDoPedido && !contratoDoPedido.seguro_ativado) {
          contratoId = contratoDoPedido.id;
        }
      }
      if (contratoId) {
        try {
          await ativarSeguroAposPrimeiraParcela(contratoId, dataPag);
        } catch (e) {
          console.error("Erro ao ativar seguro do contrato:", e);
        }
      }

      return { pedidoId, tinhaComprovante: !!primeiraTx?.nota_fiscal_url };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["contas-a-receber"] });
      queryClient.invalidateQueries({ queryKey: ["transacoes_financeiras"] });
      queryClient.invalidateQueries({ queryKey: ["parcelas"] });
      queryClient.invalidateQueries({ queryKey: ["contratos"] });
      queryClient.invalidateQueries({ queryKey: ["agenda"] });
    },
  });
}

export function useCancelarConfirmacaoVendaPedido() {
  const queryClient = useQueryClient();
  const { user, isAdmin } = useAuth();

  return useMutation({
    mutationFn: async ({
      pedidoId,
      motivo,
    }: {
      pedidoId: number;
      motivo: string;
    }) => {
      if (!isAdmin()) throw new Error("Apenas administradores podem cancelar a confirmação.");
      if (!user?.id) throw new Error("Usuário não autenticado.");
      const motivoLimpo = motivo.trim();
      if (!motivoLimpo) throw new Error("Informe o motivo do cancelamento.");

      const { data: pedido, error: errPed } = await supabase
        .from("pedidos")
        .select("id, confirmacao_status")
        .eq("id", pedidoId)
        .single();
      if (errPed) throw errPed;
      if (pedido.confirmacao_status !== "aguardando") {
        throw new Error("Pedido não está aguardando confirmação.");
      }

      const agora = new Date().toISOString();
      const { error: errUpd } = await supabase
        .from("pedidos")
        .update({
          confirmacao_status: "cancelada",
          confirmacao_cancelado_por: user.id,
          confirmacao_cancelado_em: agora,
          confirmacao_motivo_cancelamento: motivoLimpo,
          status: "cancelado",
        })
        .eq("id", pedidoId)
        .eq("confirmacao_status", "aguardando");
      if (errUpd) throw errUpd;

      await supabase
        .from("transacoes_financeiras")
        .update({ status: "cancelado" })
        .eq("pedido_id", pedidoId)
        .eq("status", "pendente");

      await supabase
        .from("parcelas")
        .update({ status: "cancelado" })
        .eq("pedido_id", pedidoId)
        .eq("status", "pendente");

      return pedidoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["pedidos"] });
      queryClient.invalidateQueries({ queryKey: ["contas-a-receber"] });
      queryClient.invalidateQueries({ queryKey: ["transacoes_financeiras"] });
      queryClient.invalidateQueries({ queryKey: ["parcelas"] });
    },
  });
}